import Phaser from 'phaser'
import { seasonName, t } from '../../i18n'
import { audio } from '../JKGame_audio'
import { isBackendEnabled } from '../../services/JKGame_api'
import { apiAccountBattlepass, apiAccountProgress, apiAccountWallet, apiDaily, apiSeason, dailySeed, getHandle, todayISO } from '../../services/JKGame_economy'
import { getMetaProgressSummary } from '../../services/JKGame_metaProgress'
import { mountMenuHubOverlay, type MenuHubOverlayHandle } from '../../ui/JKGame_menuHubOverlay'
import { matchController } from '../../services/JKGame_MatchController'
import { getUnlocked } from '../../story/JKGame_progress'
import { CHAPTERS } from '../../story/JKGame_story'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { COLORS, FONT, makeButton } from '../JKGame_ui'

export class JKGame_MenuScene extends Phaser.Scene {
  private view!: Phaser.GameObjects.Container
  private hubOverlay?: MenuHubOverlayHandle
  private toastTxt?: Phaser.GameObjects.Text
  private toastTween?: Phaser.Tweens.Tween
  private alive = true // false after SHUTDOWN; guards async loaders touching dead objects
  private rootActions: Array<() => void> = [] // root-menu button actions (for number-key shortcuts)
  private rootActive = false // true only while the root menu (not the chapter list) is showing
  private navigating = false // true once a scene transition begins; blocks a double-fire leave
  private readonly keyOne = (): void => this.selectRoot(0)
  private readonly keyTwo = (): void => this.selectRoot(1)
  private readonly keyThree = (): void => this.selectRoot(2)
  private readonly keyFour = (): void => this.selectRoot(3)
  private readonly keyFive = (): void => this.selectRoot(4)
  private readonly keySix = (): void => this.selectRoot(5)
  private readonly keyEsc = (): void => {
    if (!this.rootActive) this.showRoot()
  }

  constructor() {
    super('Menu')
  }

  /** Transient localized message (e.g. why a locked chapter can't open). */
  private toast(msg: string): void {
    this.toastTween?.stop()
    this.toastTxt?.destroy()
    const txt = this.add
      .text(DESIGN_WIDTH / 2, DESIGN_HEIGHT - 70, msg, {
        fontFamily: FONT, fontSize: '16px', color: COLORS.bad, align: 'center',
        backgroundColor: '#0b0e14cc', padding: { x: 12, y: 6 },
        wordWrap: { width: DESIGN_WIDTH - 80, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setDepth(50)
    this.toastTxt = txt
    this.toastTween = this.tweens.add({ targets: txt, alpha: 0, delay: 1400, duration: 500, onComplete: () => txt.destroy() })
  }

  create(): void {
    this.alive = true
    this.hubOverlay?.destroy()
    this.hubOverlay = undefined
    this.navigating = false // reset on (re)entry — Phaser reuses the scene instance across restarts
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.hubOverlay?.destroy()
      this.hubOverlay = undefined
      this.input.keyboard?.off('keydown-ONE', this.keyOne)
      this.input.keyboard?.off('keydown-TWO', this.keyTwo)
      this.input.keyboard?.off('keydown-THREE', this.keyThree)
      this.input.keyboard?.off('keydown-FOUR', this.keyFour)
      this.input.keyboard?.off('keydown-FIVE', this.keyFive)
      this.input.keyboard?.off('keydown-SIX', this.keySix)
      this.input.keyboard?.off('keydown-ESC', this.keyEsc)
    })
    // Desktop number-key shortcuts for the root menu (1-4); ignored on the chapter list.
    this.input.keyboard?.off('keydown-ONE', this.keyOne).on('keydown-ONE', this.keyOne)
    this.input.keyboard?.off('keydown-TWO', this.keyTwo).on('keydown-TWO', this.keyTwo)
    this.input.keyboard?.off('keydown-THREE', this.keyThree).on('keydown-THREE', this.keyThree)
    this.input.keyboard?.off('keydown-FOUR', this.keyFour).on('keydown-FOUR', this.keyFour)
    this.input.keyboard?.off('keydown-FIVE', this.keyFive).on('keydown-FIVE', this.keyFive)
    this.input.keyboard?.off('keydown-SIX', this.keySix).on('keydown-SIX', this.keySix)
    // Esc returns from the chapter list to the root menu (parity with other scenes' back-key).
    this.input.keyboard?.off('keydown-ESC', this.keyEsc).on('keydown-ESC', this.keyEsc)
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(260, 0, 0, 0) // gentle entrance; auto-cleared on scene restart
    this.showRoot()
  }

  private async loadSeason(overlay: MenuHubOverlayHandle): Promise<void> {
    const handle = getHandle()
    overlay.setAccountLine(t('menu.loading')) // pending feedback while the account loads
    try {
      const lines: string[] = []
      // Wallet balance (best-effort).
      try {
        const w = await apiAccountWallet(handle)
        lines.push(t('gacha.balance', { gem: w.gem, coin: w.coin }))
      } catch {
        // balance is best-effort; the season summary follows.
      }
      const p = await apiAccountProgress(handle)
      lines.push(t('menu.season', { lv: p.season.level, into: p.season.into, next: p.season.next }))
      // Season countdown (account-less, best-effort).
      try {
        const s = await apiSeason()
        const sname = seasonName(s.id) // localized season identity (e.g. Tidefall)
        if (s.active) lines.push(`${sname}・${t('menu.seasonCountdown', { days: s.days_left })}`)
        else if (s.days_left > 0) lines.push(`${sname}・${t('menu.seasonUpcoming')}`)
        else lines.push(t('menu.seasonEnded'))
      } catch {
        // countdown is best-effort; the season level already rendered.
      }
      try {
        const bp = await apiAccountBattlepass(handle)
        if (bp.claimable.length > 0) lines.push(t('bp.hint', { n: bp.claimable.length }))
      } catch {
        // BP nudge is best-effort; the season line already rendered.
      }
      if (!this.alive || this.hubOverlay !== overlay) return // navigated before the data arrived
      overlay.setAccountLine(lines.join('\n'))
    } catch {
      if (!this.alive || this.hubOverlay !== overlay) return
      // Offline / not reachable → leave the summary empty (no raw key, no crash).
      overlay.setAccountLine('')
    }
  }

  private resetView(): void {
    this.view?.destroy()
    this.view = this.add.container(0, 0)
  }

  private destroyHubOverlay(): void {
    this.hubOverlay?.destroy()
    this.hubOverlay = undefined
  }

  private showRoot(): void {
    this.resetView()
    this.destroyHubOverlay()
    const meta = getMetaProgressSummary()
    // Actions also bound to number keys 1-6 (desktop) via selectRoot().
    this.rootActions = [
      () => this.showChapters(),
      () => this.startBattleMode(false),
      () => this.startBattleMode(true),
      () => this.go('Collection'),
      () => this.go('Progress'),
      () => this.go('Shop'),
    ]
    this.drawHubBackground()
    const collectionSub = meta.unlockableCount > 0
      ? t('menu.collection.ready', { n: meta.unlockableCount })
      : meta.missingForNext !== null
        ? t('menu.collection.next', { n: meta.missingForNext })
        : t('menu.collection.complete')
    this.hubOverlay = mountMenuHubOverlay({
      title: t('app.title'),
      subtitle: t('app.subtitle'),
      accountLine: isBackendEnabled() ? t('menu.loading') : '',
      todayLabel: t('menu.hubToday'),
      pitch: t('menu.hubPitch'),
      footer: t('menu.hubFooter'),
      modes: [
        { index: 0, badge: t('menu.story.badge'), title: t('menu.story'), body: t('menu.story.desc'), meta: t('menu.story.meta'), tone: 'story', onTap: () => this.tapRoot(0) },
        { index: 1, badge: t('menu.survival.badge'), title: t('menu.survival'), body: t('menu.survival.desc'), meta: t('menu.survival.meta'), tone: 'survival', onTap: () => this.tapRoot(1) },
        { index: 2, badge: t('menu.daily.badge'), title: t('menu.daily'), body: t('menu.daily.desc'), meta: t('menu.daily.meta'), tone: 'daily', onTap: () => this.tapRoot(2) },
      ],
      minis: [
        { index: 3, label: t('menu.collection'), sub: collectionSub, tone: 'collection', onTap: () => this.tapRoot(3) },
        { index: 4, label: t('menu.progress'), sub: t('menu.progress.sub'), tone: 'progress', onTap: () => this.tapRoot(4) },
        { index: 5, label: t('menu.shop'), sub: t('menu.shop.sub'), tone: 'shop', onTap: () => this.tapRoot(5) },
      ],
      gachaLabel: t('menu.gacha'),
      settingsLabel: t('menu.settings'),
      onGacha: () => { audio.play('uiTap'); this.go('Gacha') },
      onSettings: () => { audio.play('uiTap'); this.go('Settings') },
      onAccount: () => { audio.play('uiTap'); this.go('Progress') },
    })
    if (isBackendEnabled()) void this.loadSeason(this.hubOverlay)
    this.rootActive = true
  }

  private drawHubBackground(): void {
    const bg = this.add.graphics()
    bg.fillStyle(0x080b12, 1).fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT)
    bg.fillStyle(0x123421, 0.45).fillCircle(DESIGN_WIDTH - 54, 110, 150)
    bg.fillStyle(0x1b2f58, 0.42).fillCircle(18, 304, 180)
    bg.fillStyle(0x090d15, 0.78).fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT)
    this.view.add(bg)
  }

  /** Run a root-menu action by index (number-key shortcut); ignored off the root view. */
  private selectRoot(i: number): void {
    if (this.rootActive) this.rootActions[i]?.()
  }

  private tapRoot(i: number): void {
    audio.play('uiTap')
    this.rootActions[i]?.()
  }

  /** Guarded scene transition — ignores a repeat trigger (double-tap, or number-key then
   *  tap) once a leave is already underway. Intra-menu view swaps (showRoot/showChapters)
   *  are NOT routed through this, so navigating within the menu stays free. */
  private go(key: string, data?: object): void {
    if (this.navigating) return
    this.navigating = true
    this.scene.start(key, data)
  }

  private startBattleMode(ranked: boolean): void {
    matchController.setRanked(ranked)
    // Guard the double-fire: without it, a quick double-tap (or "2" then a tap) would spin
    // up TWO backend sessions and race two scene.start('Explore') calls (cf. Result #195).
    if (this.navigating) return
    this.navigating = true
    void this.launchBattle()
  }

  /** Resolve the seed (ranked = the shared DAILY challenge seed so everyone faces the identical
   *  island/bots → fair async board; casual = a fresh random seed), then start the match. */
  private async launchBattle(): Promise<void> {
    let seed = (Date.now() >>> 0) || 1
    if (matchController.isRanked()) {
      try {
        seed = (await apiDaily()).seed // server-authoritative daily seed
      } catch {
        seed = dailySeed(todayISO()) // offline: same per-day seed via the local mirror
      }
    }
    if (!this.alive) return // navigated away while awaiting /daily
    matchController.newMatch(seed) // local sim drives the playable match
    void matchController.startBackendMatch(seed) // authoritative backend session (no-op unless flag on)
    this.scene.start('Explore')
  }

  private showChapters(): void {
    this.resetView()
    this.destroyHubOverlay()
    this.rootActive = false // number-key root shortcuts don't apply on the chapter list
    const cx = DESIGN_WIDTH / 2
    const unlocked = getUnlocked()
    // At-a-glance story progress: count exactly the ✓ marks shown below (a chapter is
    // cleared when it is playable AND the player has advanced past it). Appended to the
    // header so the screen says "how far am I" without adding a line to the tight layout.
    const cleared = CHAPTERS.filter((c) => !!c.start && c.index < unlocked).length
    // A 🏆 crowns the header once every chapter is cleared — a small completion reward
    // (emoji, no new textKey); only the count drives it, so it appears exactly at 100%.
    const allClear = cleared === CHAPTERS.length
    const header = `${t('menu.chapterSelect')}　·　${t('menu.chaptersCleared', { done: cleared, total: CHAPTERS.length })}${allClear ? ' 🏆' : ''}`
    const headerTxt = this.add.text(cx, 150, header, { fontFamily: FONT, fontSize: '22px', color: COLORS.text, fontStyle: 'bold' }).setOrigin(0.5)
    const maxHeaderW = DESIGN_WIDTH - 32 // shrink a wide localized header so the count never clips (parity with titles)
    if (headerTxt.width > maxHeaderW) headerTxt.setScale(maxHeaderW / headerTxt.width)
    this.view.add(headerTxt)

    CHAPTERS.forEach((ch, i) => {
      const y = 200 + i * 80
      const playable = !!ch.start && ch.index <= unlocked
      const completed = playable && ch.index < unlocked // finishing N unlocks N+1 ⇒ N is done
      // The localized chapter title already carries its number/name (e.g. "Chapter 2:
      // Trust and Betrayal" / "第二章：信任與背叛"), so no separate "Chapter N" tag — it
      // only duplicated the prefix. Status mark + title reads clean.
      const title = t(ch.titleKey)
      // ✓ done (replayable) · ▶ next-to-play (the frontier, guides the player) · 🔒 locked.
      const frontier = playable && !completed // index === unlocked: the next chapter to play
      const mark = !playable ? '🔒 ' : completed ? '✓ ' : '▶ '
      const label = `${mark}${title}`
      this.view.add(
        makeButton(this, cx, y, {
          width: DESIGN_WIDTH - 60,
          height: 66,
          label,
          fontSize: 19,
          enabled: playable,
          // The frontier gets a brighter fill so the eye lands on "play this next".
          fill: frontier ? 0x2e4a6e : ch.endsWithMatch ? 0x2a3a52 : 0x223049,
          onTap: () => this.go('Story', { chapterId: ch.id }),
          onDisabledTap: () => this.toast(t('menu.chapterLocked')),
        }),
      )
    })

    this.view.add(makeButton(this, cx, 200 + CHAPTERS.length * 80 + 8, { width: 200, height: 52, label: t('menu.back'), fontSize: 18, onTap: () => this.showRoot() }))
  }
}
